// Check-email interstitial. Shown after a magic link is emailed: it confirms the
// destination address, names the sender + subject (so the recipient can tell the
// real email from a phish), shows the non-secret reference code, and offers a
// cooldown-guarded resend + a "use a different email" escape hatch.
//
// There is deliberately no spinner — the next action happens in the inbox, not
// on this screen. If verification completes in another tab, a BroadcastChannel
// message advances this tab automatically.
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Container, Paper, Typography, Button, Alert, Chip, Stack,
} from '@mui/material';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import { resendMagicLink, resetContact, fetchIntakeSession } from '../store/contact-slice.js';
import { selectSettings } from '../store/settings-slice.js';
import ThemeModeToggle from '../components/ThemeModeToggle.jsx';
import SanbornLogo from '../components/SanbornLogo.jsx';

const EMAIL_SUBJECT = 'Verify your email to continue your Sanborn imagery request';
// Escalating resend cooldowns (seconds): each press waits longer.
const COOLDOWNS = [45, 90, 180];

const CheckEmailPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const settings = useSelector(selectSettings);
  const {
    sent, verified, blocked, pendingEmail, contact, referenceCode, error,
  } = useSelector((s) => s.contact);

  const headingRef = useRef(null);
  const [secondsLeft, setSecondsLeft] = useState(COOLDOWNS[0]);
  const attemptRef = useRef(0);

  useEffect(() => { headingRef.current?.focus(); }, []);

  // Route guards: a verified visitor moves on; a direct hit with nothing in
  // flight goes back to the form; a block diverts to the concierge page.
  useEffect(() => {
    if (verified) navigate('/intake', { replace: true });
    else if (blocked) navigate('/blocked', { replace: true });
    else if (!sent) navigate('/', { replace: true });
  }, [verified, blocked, sent, navigate]);

  // Cooldown ticker.
  useEffect(() => {
    if (secondsLeft <= 0) return undefined;
    const id = setInterval(() => setSecondsLeft((n) => Math.max(0, n - 1)), 1000);
    return () => clearInterval(id);
  }, [secondsLeft]);

  // Cross-tab: if the link is opened in another tab, advance this one too.
  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return undefined;
    const channel = new BroadcastChannel('intake-verification');
    channel.onmessage = (event) => {
      if (event.data === 'verified') dispatch(fetchIntakeSession());
    };
    return () => channel.close();
  }, [dispatch]);

  const handleResend = () => {
    if (secondsLeft > 0 || !contact) return;
    dispatch(resendMagicLink(contact));
    attemptRef.current += 1;
    setSecondsLeft(COOLDOWNS[Math.min(attemptRef.current, COOLDOWNS.length - 1)]);
  };

  const handleDifferentEmail = () => {
    dispatch(resetContact());
    navigate('/', { replace: true });
  };

  const address = pendingEmail || contact?.email;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', position: 'relative' }}>
      <Box sx={{ position: 'absolute', top: 12, right: 16 }}><ThemeModeToggle /></Box>
      <Container maxWidth="sm" sx={{ py: { xs: 6, md: 10 } }}>
        <Box sx={{ color: 'text.primary', mb: 3, textAlign: 'center' }}>
          <SanbornLogo height={72} href="https://sanborn.com/" />
        </Box>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 4 },
            borderRadius: 3,
            border: 1,
            borderColor: 'divider',
            borderTop: 3,
            borderTopColor: 'primary.main',
          }}
        >
          <Stack spacing={2}>
            <Box sx={{ textAlign: 'center' }}>
              <MarkEmailReadOutlinedIcon color="primary" sx={{ fontSize: 48 }} />
              <Typography
                ref={headingRef}
                tabIndex={-1}
                variant="h4"
                component="h1"
                sx={{ mt: 1, fontWeight: 700, outline: 'none' }}
              >
                Check your email to continue
              </Typography>
            </Box>

            <Typography variant="body1" sx={{ textAlign: 'center' }}>
              We sent a secure verification link to
              {' '}
              <Box component="strong" sx={{ wordBreak: 'break-all' }}>{address}</Box>
              . Open it to continue building your imagery request.
            </Typography>

            <Alert severity="info" sx={{ alignItems: 'flex-start' }}>
              Look for an email from
              {' '}
              <strong>{settings.program_name || 'Sanborn'}</strong>
              {' '}
              with the subject &ldquo;
              {EMAIL_SUBJECT}
              &rdquo;. The link expires in 30 minutes.
            </Alert>

            {referenceCode && (
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                  Verify it matches this reference code:
                </Typography>
                <Chip label={referenceCode} sx={{ fontWeight: 700, letterSpacing: '0.15em' }} />
              </Box>
            )}

            {error && <Alert severity="error">{error}</Alert>}

            <Box sx={{ textAlign: 'center' }}>
              <Button
                variant="outlined"
                onClick={handleResend}
                disabled={secondsLeft > 0 || !contact}
              >
                {secondsLeft > 0 ? `Resend link in ${secondsLeft}s` : 'Resend link'}
              </Button>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
              Wrong address?
              {' '}
              <Button variant="text" size="small" onClick={handleDifferentEmail} sx={{ verticalAlign: 'baseline' }}>
                Use a different email
              </Button>
            </Typography>
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
};

export default CheckEmailPage;
